import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ORG_STORAGE_KEY } from "../lib/api";
import { Button, Card, Field, Select, TextField, ErrorState } from "../components/ui";

export default function Onboarding() {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    legalName: "",
    orgCustomerId: "",
    baseCurrency: "USD",
    fiscalYearStartMonth: 1,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/auth/orgs", {
        name: form.name,
        legalName: form.legalName || undefined,
        orgCustomerId: form.orgCustomerId || undefined,
        baseCurrency: form.baseCurrency,
        fiscalYearStartMonth: Number(form.fiscalYearStartMonth),
      }),
    onSuccess: (res) => {
      localStorage.setItem(ORG_STORAGE_KEY, res.id);
      qc.invalidateQueries({ queryKey: ["session"] });
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50 to-white p-4">
      <Card className="w-full max-w-lg p-8">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500 font-bold text-white">
            GL
          </span>
          <div>
            <h1 className="text-xl font-semibold">Create your organization</h1>
            <p className="text-sm text-gray-500">
              Set up a books file to start tracking your accounting.
            </p>
          </div>
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Organization name">
            <TextField
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme, Inc."
            />
          </Field>
          <Field label="Legal name (optional)">
            <TextField
              value={form.legalName}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
            />
          </Field>
          <Field
            label="External org_customer_id (optional)"
            hint="64-bit identifier from your other system. Stored exactly as entered."
          >
            <TextField
              inputMode="numeric"
              value={form.orgCustomerId}
              onChange={(e) =>
                setForm({
                  ...form,
                  orgCustomerId: e.target.value.replace(/[^0-9]/g, ""),
                })
              }
              placeholder="e.g. 90071992547409920"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field required label="Base currency">
              <Select
                value={form.baseCurrency}
                onChange={(e) =>
                  setForm({ ...form, baseCurrency: e.target.value })
                }
              >
                {["USD", "EUR", "GBP", "CAD", "AUD"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </Field>
            <Field required label="Fiscal year starts">
              <Select
                value={form.fiscalYearStartMonth}
                onChange={(e) =>
                  setForm({
                    ...form,
                    fiscalYearStartMonth: Number(e.target.value),
                  })
                }
              >
                {[
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December",
                ].map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {create.error && <ErrorState error={create.error} />}

          <Button type="submit" disabled={create.isPending} className="w-full">
            {create.isPending ? "Creating…" : "Create organization"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
